import { forwardRef, createElement } from 'react';
import cn from 'classnames';
import { Backdrop } from '../../Backdrop/Backdrop.js';
import { IconButton } from '../../IconButton/IconButton.js';
import { useLocalization } from '../../Provider/Provider.js';
import { useProxiedRef } from '../../../utils/hooks/useProxiedRef.js';
import { Title as Title$1, Close as Close$1, Portal, Overlay, Content as Content$1 } from '@radix-ui/react-dialog';
import { useCurrentDialog } from '../Context.js';
import { useDraggable } from '../../../utils/hooks/useDraggable.js';
import { useCurrentMenu } from '../../Menu/Context.js';

const Title = /*#__PURE__*/forwardRef(function DialogTitle(props, ref) {
  const className = cn('text-center', props.className);
  return createElement(Title$1, Object.assign({}, props, {
    className: className,
    ref: ref
  }));
});
const Footer = /*#__PURE__*/forwardRef(function DialogFooter(props, ref) {
  const className = cn('mt-8 flex justify-end', props.className);
  return createElement("div", Object.assign({}, props, {
    className: className,
    ref: ref
  }), props.children);
});
const Close = /*#__PURE__*/forwardRef(function DialogClose(props, ref) {
  const dialog = useCurrentDialog();
  return createElement(Close$1, Object.assign({
    onClick: dialog.onClose
  }, props, {
    ref: ref,
    asChild: true
  }));
});
const RenderPropWrapper = /*#__PURE__*/forwardRef(function RenderPropWrapper({
  children,
  onClick,
  renderProps
}, ref) {
  const close = () => {
    onClick(new CustomEvent('close'));
  };

  return children({
    close,
    ref,
    ...renderProps
  });
});
const Content = /*#__PURE__*/forwardRef(function DialogContent(props, ref) {
  const dialog = useCurrentDialog();
  const internalRef = useProxiedRef(ref);
  const {
    position,
    handleProps: dragHandleProps
  } = useDraggable(internalRef);
  const {
    texts
  } = useLocalization(); // if the dialog was opened by a menu, we need to close the menu when the dialog closes
  // the menu is still open (and mounted) because it is the trigger for the dialog

  const menu = useCurrentMenu();
  let handleCloseAutoFocus;

  if (menu) {
    handleCloseAutoFocus = () => menu.close();
  }

  const className = cn('bg-white rounded relative mt-16 mx-auto animate-[fade-in_150ms] z-[997]', {
    'w-128 text-center justify-center': dialog.size === 'dialog',
    'w-xs': dialog.size === 'xs',
    'w-sm': dialog.size === 'sm',
    'w-md': dialog.size === 'md',
    'w-lg': dialog.size === 'lg'
  });
  const containerClassName = cn('bg-white p-6 rounded relative z-[999]', 'shadow-[0_6px_9px_0_rgba(89,85,98,0.3),0_0_1px_0_rgba(89,85,98,0.2)]', {
    'rounded-b-none': !!dialog.elements.extra
  }, props.className);

  const handleEscapeKeyDown = event => {
    if (!dialog.closeOnEscape) {
      event.preventDefault();
    } else if (dialog.onClose) {
      dialog.onClose();
    }
  }; // the chosen behaviour in taco is that outside clicks do not close the dialog


  const handleInteractOutside = event => event.preventDefault();

  let output;

  if (typeof props.children === 'function') {
    output = createElement(Close$1, {
      asChild: true
    }, createElement(RenderPropWrapper, {
      renderProps: {
        drawer: dialog.drawer
      }
    }, props.children));
  } else {
    output = props.children;
  }

  return createElement(Portal, null, createElement(Overlay, {
    asChild: true
  }, createElement(Backdrop, null, createElement(Content$1, Object.assign({}, props, {
    className: className,
    onEscapeKeyDown: handleEscapeKeyDown,
    onInteractOutside: handleInteractOutside,
    onCloseAutoFocus: handleCloseAutoFocus,
    ref: ref,
    style: { ...props.style,
      left: dialog.draggable ? `${position.x}px` : undefined,
      top: dialog.draggable ? `${position.y}px` : undefined
    }
  }), createElement("div", {
    className: containerClassName,
    "data-taco": "dialog"
  }, output, dialog.draggable && createElement("div", Object.assign({}, dragHandleProps, {
    "aria-label": texts.dialog.drag,
    className: "yt-dialog__drag absolute-center-x bg-grey-light top-1.5 h-3 w-24 cursor-move rounded text-center"
  })), dialog.showCloseButton ? createElement(Close$1, {
    onClick: dialog.onClose,
    asChild: true
  }, createElement(IconButton, {
    appearance: "discrete",
    "aria-label": texts.dialog.close,
    className: "absolute top-0 right-0 mt-2 mr-2",
    icon: "close"
  })) : null), dialog.elements.drawer, dialog.elements.extra))));
});

export { Close, Content, Footer, Title };
//# sourceMappingURL=Content.js.map
